/**
 * Creates a temporary message object for optimistic UI updates.
 * @param {Object} messagePayload - The raw payload for the new message.
 * @param {Object} currentConversation - The current conversation object from the store state.
 * @param {Object} currentUser - The current user object from the auth store.
 * @returns {Object} The temporary message object.
 */
export function createTemporaryMessage(
    messagePayload,
    currentConversation,
    currentUser
) {
    const tempId = `temp_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 7)}`

    console.log('messagePayload for optimistic message', messagePayload)

    const tempMessage = {
        _id: tempId, // Use temp ID locally
        tempId: tempId, // Store tempId explicitly for replacement later
        conversationId: currentConversation._id,
        sender: currentUser._id,
        receiver: currentConversation.participantDetails.find(
            (p) => p.userId !== currentUser._id
        )?.userId, // Find receiver ID
        content: messagePayload.content, // Assuming payload has content
        contentType: messagePayload.contentType || 'text', // Assuming payload has contentType
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        readBy: [],
        receivedBy: [],
        translations: {},
        isOptimistic: true,
        error: false, // Initialize error as false
        ...(messagePayload.contentType === 'image' && {
            localFileUrl: messagePayload.localFileUrl, // Used for local display before upload
        }),
        ...(messagePayload.contentType === 'audio' && {
            duration: messagePayload.duration || 0,
            localFileUrl: messagePayload.localFileUrl, // Store local URL for audio too if needed
        }),
        ...(messagePayload.contentType === 'video' && {
            duration: messagePayload.duration || 0,
            localFileUrl: messagePayload.localFileUrl, // Store local URL for video too
        }),
    }

    // Store the blob URL globally for potential revocation, keyed by tempId
    if (tempMessage.localFileUrl && tempMessage.contentType !== 'text') {
        if (!window._pendingBlobURLs) window._pendingBlobURLs = {}
        window._pendingBlobURLs[tempId] = tempMessage.localFileUrl
        console.log(
            `Stored blob URL for temp ${tempMessage.contentType} ${tempId}`
        )
    }

    return tempMessage
}

/**
 * Handles the failure of sending an optimistic message.
 * Marks the message as failed in the currentMessages array and revokes any associated blob URL.
 * Modifies the currentMessages array directly.
 * @param {Array} currentMessages - The reactive array of current messages from the store state.
 * @param {string} tempId - The temporary ID of the message that failed.
 */
export function handleOptimisticSendFailure(currentMessages, tempId) {
    const index = currentMessages.findIndex((m) => m._id === tempId)
    if (index !== -1) {
        // Mark as failed
        // Direct modification should work with Vue 3 reactive arrays.
        currentMessages[index].isOptimistic = false
        currentMessages[index].error = true // Add an error flag
        console.log(`Marked optimistic message ${tempId} as failed.`)

        // Revoke blob URL on failure too, using the globally stored mapping
        if (window._pendingBlobURLs && window._pendingBlobURLs[tempId]) {
            console.log(`Revoking blob URL for failed temp message ${tempId}`)
            URL.revokeObjectURL(window._pendingBlobURLs[tempId])
            delete window._pendingBlobURLs[tempId]
        }
    } else {
        console.warn(
            `Failed optimistic message ${tempId} not found in current list.`
        )
    }
}
